import { LEGAL_CONTACT_EMAIL } from "@/utils/legalDocs";
import { SquareDealLink } from "@/components/SquareDealLink";

export default function TermsOfServiceContent() {
  return (
    <>
      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Agreement</h2>
        <p>
          These Terms of Service (&quot;Terms&quot;) govern your use of TerraKronos (&quot;we,&quot;
          &quot;us,&quot; or &quot;our&quot;) at terrakronos.com, staging.terrakronos.com when we
          offer it, and related applications (the &quot;Service&quot;). By creating an account or
          using the Service, you agree to these Terms. If you do not agree, do not use the Service.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">What TerraKronos provides</h2>
        <p>
          TerraKronos is software for Hawaii real estate professionals. It helps you organize deal
          timelines, deal facts, documents, shares, templates, Hawaii calculators, Team and
          coordinator workflows, and, where we have permission, fill a purchase-contract form and
          create a PDF. Features depend on your subscription plan. Some forms or features may be
          unavailable, in preview, or marked coming soon. We may change or discontinue features
          with reasonable notice when practical.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Not legal or financial advice</h2>
        <p>
          The Service is for organizational and productivity purposes only. It does not provide legal,
          tax, brokerage, or financial advice. You are responsible for compliance with applicable laws,
          board rules, and contract requirements. Deadlines, calculators, and filled forms in the app
          are aids only. You must verify all dates, amounts, checkboxes, and obligations against the
          official paper and your deal. A Hawaii calendar day in the app ends at the end of that day
          in Hawaii, not at UTC midnight.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Official forms</h2>
        <p>
          When the Service fills a purchase contract, it is filling a third-party form we are allowed
          to use (for example <SquareDealLink />, with the credit that form requires). Those printed forms
          belong to their publishers, not to TerraKronos. We do not claim to replace the official
          form. You must confirm the PDF matches the current official paper and what your clients
          agreed to. A HAR fill, if offered later, needs that publisher&apos;s permission and is not
          promised by these Terms.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Accounts</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>You must provide accurate account information and keep it current.</li>
          <li>
            You may sign in with Google or with a one-time email sign-in link. We do not offer a
            new email-and-password account. Third-party sign-in is also subject to that
            provider&apos;s terms.
          </li>
          <li>
            You are responsible for activity under your account and for protecting your devices,
            your email inbox, share links, calendar subscribe links, and encryption recovery
            materials.
          </li>
          <li>You must be at least 18 years old and authorized to use the Service for your business or employment.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Teams, shares, and calendar links</h2>
        <p>
          If you add people to a Team or to a deal, you are responsible for who you invite and what
          they can see. A share link or calendar subscribe link works like a secret. Anyone who has
          that link can open the view you allowed until you change or remove it. A Team people list
          is shared with that Team. Copying a person onto a deal stamps the text onto that file.
          Later library edits do not rewrite old deals.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Subscriptions and payment</h2>
        <p>
          Paid plans are billed through Stripe. Prices, seats, and features are described at checkout
          and in the app. Subscriptions renew according to the billing period you select unless you
          cancel through the Stripe billing portal or as we describe in Settings. Fees are generally
          non-refundable except where required by law or stated otherwise in writing. Plan limits
          (for example number of deals or seats) are enforced in the Service.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Acceptable use</h2>
        <p className="mb-3">You agree not to:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Use the Service for unlawful, fraudulent, or harmful purposes</li>
          <li>Attempt to access another user&apos;s data without permission</li>
          <li>Interfere with or disrupt the Service, including by automated scraping or abuse</li>
          <li>Upload malware or content you do not have the right to store or share</li>
          <li>Misrepresent your identity or affiliation</li>
          <li>Share sign-in links, share links, or calendar links with people who should not have that access</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Your content</h2>
        <p>
          You retain ownership of information and files you submit. You grant us a limited license to
          host, process, and display your content solely to operate the Service, including backups,
          security, filled PDFs, emails, shares you create, and spreadsheet downloads you request.
          You represent that you have the rights needed for any content you upload, including client
          personal information, and that sharing with coordinators, teammates, or other invitees is
          permitted.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Confidentiality</h2>
        <p>
          Deal data may include sensitive client and business information. You are responsible for
          how you use sharing, invitations, people lists, and document access. We implement technical
          safeguards, but you control who you invite and what you upload.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Intellectual property</h2>
        <p>
          The Service, including our software, branding, and the TerraKronos playbooks we provide
          (such as starter timelines), is owned by us or our licensors. Official purchase-contract
          forms remain the property of their publishers. These Terms do not grant you rights to our
          trademarks or to copy the Service except as allowed through normal use.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Downloads and backups</h2>
        <p>
          You may download a spreadsheet of your people list and deal clocks. That is a convenience,
          not a complete backup of your account, documents, or messages. You are responsible for
          keeping copies you need outside the Service.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Disclaimer of warranties</h2>
        <p>
          THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot; TO THE MAXIMUM EXTENT
          PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT GUARANTEE THAT THE SERVICE
          WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT DEADLINES, CALCULATIONS, OR FILLED FORMS WILL BE
          ACCURATE OR COMPLETE.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Limitation of liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE AND OUR SUPPLIERS WILL NOT BE LIABLE FOR ANY
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, DATA,
          OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM RELATING
          TO THE SERVICE IS LIMITED TO THE GREATER OF (A) AMOUNTS YOU PAID US IN THE TWELVE MONTHS
          BEFORE THE CLAIM OR (B) ONE HUNDRED U.S. DOLLARS ($100).
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Indemnity</h2>
        <p>
          You will defend and indemnify us against claims arising from your use of the Service, your
          content, or your violation of these Terms, except to the extent caused by our gross
          negligence or willful misconduct.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Termination</h2>
        <p>
          You may stop using the Service at any time. Request account deletion from Settings → Legal.
          We review that request, then erase the login as described in our Privacy Policy. We may
          suspend or terminate access if you breach these Terms or if required for security or legal
          reasons. Upon termination, your right to use the Service ends; we may delete or retain data
          as described in our Privacy Policy.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Governing law</h2>
        <p>
          These Terms are governed by the laws of the State of Hawaii, without regard to conflict of
          law rules. Disputes will be brought in the state or federal courts located in Hawaii, unless
          applicable law requires otherwise.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Changes</h2>
        <p>
          We may update these Terms. We will post the new version on this page and update the
          &quot;Last updated&quot; date. Material changes may be communicated by email or in-app notice
          when appropriate. Continued use after changes means you accept the updated Terms.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Contact</h2>
        <p>
          Questions about these Terms:{" "}
          <a href={`mailto:${LEGAL_CONTACT_EMAIL}`} className="text-primary hover:underline">
            {LEGAL_CONTACT_EMAIL}
          </a>
        </p>
      </section>
    </>
  );
}
